import { useTranslation } from "react-i18next";
import { DATE_OPTIONS } from "../../core/utils/date-utils";
import { RE_ASSESSMENT_RESULT } from "../../enums/ReAssessmentEnums";
import {
    QuickDateRange,
    QuickModalityFilter,
    QuickResetLink,
    QuickSegment,
    useAppliedFilter,
} from "../quick-filter/QuickFilterParts";
import { ExtraFiltersButton, ExtraFilterTags, isFieldActive } from "../quick-filter/ExtraFilters";
import { buildDefaultReAssessmentFilter } from "./re-assessment-filter-defaults";
import { RE_ASSESSMENT_EXTRA_FIELDS } from "./re-assessment-extra-filters";
import { RE_ASSESSMENT_DATE_TYPE } from "./re-assessment-filter-query";

// Son 15 gün sayfanın varsayılanı (bkz. re-assessment-filter-defaults). Talepler rapor/çekime göre seyrek ve
// ekran büyük ölçüde oran takibi için açılıyor → "Dün" yerine uzun aralık (3 ay) daha çok işe yarar.
const DATE_PRESETS = [
    { key: 'today', label: 'Today', option: DATE_OPTIONS.TODAY },
    { key: 'last15', label: 'Last 15D', option: DATE_OPTIONS.LAST_15_DAYS },
    { key: 'last30', label: 'Last 30D', option: DATE_OPTIONS.LAST_30_DAYS },
    { key: 'last3m', label: 'Last 3M', option: DATE_OPTIONS.LAST_3_MONTH },
];

// Sol paneldeki tarih türleri. Rapor ekranının aksine tür ayrı bir alan (dateType), aralık hep dateBegin/dateEnd:
// türü çubuktan değiştirmek tek alan yazmak demek, bu yüzden çubukta da seçilebiliyor.
const DATE_TYPE_OPTIONS = [
    { value: RE_ASSESSMENT_DATE_TYPE.REQUEST, label: 'Requested At' },
    { value: RE_ASSESSMENT_DATE_TYPE.RESOLVE, label: 'Resolved At' },
    { value: RE_ASSESSMENT_DATE_TYPE.STUDY, label: 'Study Date' },
];

/**
 * Yeniden değerlendirme grid'inin üstündeki hızlı filtre çubuğu — çekim/rapor grid'indekiyle aynı düzen ve parçalar
 * (quick-filter/). Serbest arama yok: sunucudaki ReAssessmentFilter'da searchText karşılığı bulunmuyor.
 * Sol panelde (ReAssessmentFilter) olup çubukta düğmesi olmayan her şey "Diğer filtreler"de.
 */
export default function ReAssessmentQuickFilterBar() {
    const { t } = useTranslation();
    const { filter, apply, replace, ready } = useAppliedFilter('reAssessmentFilter');

    if (!ready)
        return null;

    const dateType = filter.dateType ?? RE_ASSESSMENT_DATE_TYPE.REQUEST;
    const dateTypeLabel = DATE_TYPE_OPTIONS.find(o => o.value === dateType)?.label ?? 'Requested At';

    const extraCount = RE_ASSESSMENT_EXTRA_FIELDS.filter(field => isFieldActive(field, filter)).length;

    return (
        <div className="qf">
            <div className="qf-row">
                <div className="qf-group">
                    <QuickSegment
                        ariaLabel={t('Date Type')}
                        value={dateType}
                        onChange={(value) => apply({ dateType: value })}
                        options={DATE_TYPE_OPTIONS.map(o => ({ value: o.value, label: t(o.label) }))}
                    />
                </div>

                <QuickDateRange
                    filter={filter}
                    apply={apply}
                    beginKey="dateBegin"
                    endKey="dateEnd"
                    presets={DATE_PRESETS}
                    label={t(dateTypeLabel)}
                />

                {/* Ekranın asıl sorusu "ne oldu": açıkta bekleyenler ve raporu değişenler tek tıkta. */}
                <div className="qf-group">
                    <QuickSegment
                        ariaLabel={t('Re-Assessment Result')}
                        value={filter.outcome ?? null}
                        // Panel "Tümü"nü alanı boşaltarak ifade ediyor (allowClear) — null değil undefined yazılır.
                        onChange={(value) => apply({ outcome: value ?? undefined })}
                        options={[
                            { value: null, label: t('All') },
                            { value: RE_ASSESSMENT_RESULT.CHANGED, label: t('Report Changed') },
                            { value: RE_ASSESSMENT_RESULT.UNCHANGED, label: t('Report Unchanged') },
                            { value: RE_ASSESSMENT_RESULT.OPEN, label: t('Open') },
                        ]}
                    />
                </div>

                <QuickModalityFilter filter={filter} apply={apply} />

                <div className="qf-group">
                    <ExtraFiltersButton
                        fields={RE_ASSESSMENT_EXTRA_FIELDS}
                        filter={filter}
                        apply={apply}
                        activeCount={extraCount}
                    />
                </div>

                <QuickResetLink onClick={() => replace(buildDefaultReAssessmentFilter())} />
            </div>

            <ExtraFilterTags fields={RE_ASSESSMENT_EXTRA_FIELDS} filter={filter} apply={apply} />
        </div>
    );
}
